import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Notice, Spinner, Text } from '@heartinternet/hi-ui';
import type { SSLCheckData, SSLInfoData } from './types';
import { DEFAULT_BASE_URL, endpoints, rpcGet } from './helpers';
import { SubmissionForm } from './submission/SubmissionForm';
import { ManageView } from './manage/ManageView';
import './SSLInfoPage.css';

/** Shared services threaded to the sub-flows (endpoints + refetch — the renderInfo analogue). */
export interface SSLInfoContext {
  baseUrl: string;
  fetchFn: typeof window.fetch;
  oid: string;
  /** Re-run /info (+ /check) and re-render — the legacy renderInfo callback. */
  refetch: () => void;
  sslSummaryUrl: string;
  termsUrl: string;
  manageDomainUrl: string;
  idealPostcodesApiKey: string;
}

export interface SSLInfoPageProps {
  /** Certificate OID. Defaults to the `id` query param (matches the legacy `?id=`). */
  oid?: string;
  /** Certificate issuer name shown in the Terms section (legacy read it from #ssl-issuer). */
  sslIssuer?: string;
  /** RPC base — default `/cp/rpc/rtr/ssl`. */
  baseUrl?: string;
  /** Injectable fetch (defaults to window.fetch) — for tests. */
  fetchFn?: typeof window.fetch;
  /** Where Revoke returns to — default `/cp/manage/sslsummary`. */
  sslSummaryUrl?: string;
  /** Issuer Terms of Use link (Terms section). */
  termsUrl?: string;
  /** "Manage Domain Names" link in the DNS "Show me how" guide. */
  manageDomainUrl?: string;
  /** ideal-postcodes API key for the address finder. */
  idealPostcodesApiKey?: string;
  className?: string;
  style?: React.CSSProperties;
}

const DEFAULT_TERMS_URL =
  'https://www.digicert.com/content/dam/digicert/pdfs/legal/Certificate-Terms-of-Use.pdf';
const DEFAULT_IDEAL_KEY = 'ak_m6giwbwpMBjV2fMk6j9PN9m5LNNrP';

/** Resolve the OID from the prop or the `id` query param. */
function resolveOid(oidProp?: string): string {
  if (oidProp) return oidProp;
  /* v8 ignore next 2 -- SSR guard; window is always present in the browser/JSDOM */
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('id') ?? '';
}

export const SSLInfoPage: React.FC<SSLInfoPageProps> = ({
  oid: oidProp,
  sslIssuer = '',
  baseUrl = DEFAULT_BASE_URL,
  fetchFn = window.fetch.bind(window),
  sslSummaryUrl = '/cp/manage/sslsummary',
  termsUrl = DEFAULT_TERMS_URL,
  manageDomainUrl = '/manage/manage.cgi/domain',
  idealPostcodesApiKey = DEFAULT_IDEAL_KEY,
  className = '',
  style,
}) => {
  const oid = resolveOid(oidProp);

  const [info, setInfo] = useState<SSLInfoData | null>(null);
  const [checkData, setCheckData] = useState<SSLCheckData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const fetchFnRef = useRef(fetchFn);
  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  const refetch = useCallback(() => setReloadToken((t) => t + 1), []);

  /* eslint-disable react-hooks/set-state-in-effect -- data-fetching effect legitimately sets loading state */
  useEffect(() => {
    const controller = new AbortController();
    const fn = fetchFnRef.current;
    setLoading(true);
    setError(null);

    const load = (async () => {
      const infoRes = await rpcGet<SSLInfoData>(
        fn,
        endpoints.info(baseUrl, oid),
        controller.signal
      );
      /* v8 ignore next -- abort race after unmount; not deterministically reachable in JSDOM */
      if (controller.signal.aborted) return;
      if (infoRes.error || !infoRes.data) {
        setError(infoRes.error ?? 'We could not load this certificate.');
        setLoading(false);
        return;
      }

      const infoData = infoRes.data;
      // Only /check when PENDING (reduces backend warnings), then remap status.
      if (infoData.status === 'PENDING') {
        const checkRes = await rpcGet<SSLCheckData>(
          fn,
          endpoints.check(baseUrl, oid),
          controller.signal
        );
        /* v8 ignore next -- abort race after unmount; not deterministically reachable in JSDOM */
        if (controller.signal.aborted) return;
        if (checkRes.error || !checkRes.data) {
          setError(checkRes.error ?? 'We could not check this certificate.');
          setLoading(false);
          return;
        }
        setCheckData(checkRes.data);
        // CheckStatusMap: PENDING→PENDING, COMPLETED→ACTIVE.
        if (checkRes.data.status === 'COMPLETED') infoData.status = 'ACTIVE';
      } else {
        setCheckData(null);
      }

      setInfo(infoData);
      setLoading(false);
    })();

    // rpcGet/rpcPost absorb network errors into an {error} envelope (handled above)
    // and only ever re-throw a deliberate AbortError, so this handler is reached only
    // by an abort race after unmount — not deterministically reachable in JSDOM.
    /* v8 ignore start */
    load.catch((err: unknown) => {
      if (controller.signal.aborted) return;
      if (err instanceof Error && err.name === 'AbortError') return;
      setError('There was a server error. Please contact support.');
      setLoading(false);
    });
    /* v8 ignore stop */

    return () => controller.abort();
  }, [oid, baseUrl, reloadToken]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const classes = ['hi-ui-ssl-info-page', className].filter(Boolean).join(' ');

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className="hi-ui-ssl-info-page__message">
        <Spinner size="large" aria-label="Loading SSL" />
        <Text variant="inline">Loading SSL</Text>
      </div>
    );
  } else if (error || !info) {
    /* v8 ignore start -- error is always set before this branch; fallback guards an impossible !info+no-error state */
    const message = error ?? 'We could not load this certificate.';
    /* v8 ignore stop */
    body = (
      <div className="hi-ui-ssl-info-page__message">
        <Notice variant="error">{message}</Notice>
      </div>
    );
  } else {
    const ctx: SSLInfoContext = {
      baseUrl,
      fetchFn,
      oid,
      refetch,
      sslSummaryUrl,
      termsUrl,
      manageDomainUrl,
      idealPostcodesApiKey,
    };
    body =
      info.status === 'NEW' ? (
        <SubmissionForm info={info} ctx={ctx} sslIssuer={sslIssuer} />
      ) : (
        <ManageView info={info} checkData={checkData} ctx={ctx} />
      );
  }

  return (
    <div className={classes} style={style}>
      <Text variant="heading-1">SSL Certificates</Text>
      <div className="hi-ui-ssl-info-page__body">{body}</div>
    </div>
  );
};
